import {Color, InstancedMesh, Object3D, Vector3} from "three";

import {
  type EmitOptions,
  type OngoingEmitRequest,
  type ParticleData,
  type ParticleEffectProps,
} from "./types";
import {
  interpolateCurve,
  normalizeColorCurve,
  normalizeSizeCurve,
  random,
  randomOnSphere,
} from "./utils";

export class ParticleManager {
  private particles: ParticleData[];
  private nextParticleIndex = 0;
  private ongoingEmitRequests: OngoingEmitRequest[] = [];
  private defaultProps: Required<
    Omit<ParticleEffectProps, "children" | "zIndex">
  >;
  private zIndex: number;

  // Reusable objects to prevent garbage collection in the update loop
  private readonly tmpObject = new Object3D();
  private readonly tmpColor = new Color();
  private readonly tmpVec3 = new Vector3();

  constructor(props: ParticleEffectProps) {
    this.zIndex = props.zIndex ?? 49;

    // Set up default properties
    this.defaultProps = {
      maxParticles: props.maxParticles ?? 500,
      particleLifetime: props.particleLifetime ?? 1.0,
      lifetimeRandomness: props.lifetimeRandomness ?? 0,
      colorCurve: props.colorCurve ?? [
        {stop: 0, color: new Color("#FFFFFF")},
        {stop: 0.5, color: new Color("#F5B041")},
        {stop: 1, color: new Color("#1C1C1C")},
      ],
      sizeCurve: props.sizeCurve ?? [
        {stop: 0, size: 0.5},
        {stop: 0.2, size: 0.8},
        {stop: 1, size: 0},
      ],
      gravity: props.gravity ?? 9.81,
      velocityMultiplier: props.velocityMultiplier ?? 0,
      upwardBias: props.upwardBias ?? 0,
    };

    // Initialize particle pool
    this.particles = Array.from(
      {length: this.defaultProps.maxParticles},
      () => ({
        isActive: false,
        position: new Vector3(),
        velocity: new Vector3(),
        rotationZ: 0,
        lifetime: 0,
        maxLifetime: 0,
        gravity: 0,
        sizeCurve: [],
        colorCurve: [],
        spawnEvents: [],
        lastSpawnCheckProgress: 0,
      }),
    );
  }

  public emit = (options: EmitOptions): void => {
    // To smooth out spawning and prevent lag, we can rate-limit the emission
    if (options.spawnRate && options.spawnRate > 0) {
      this.ongoingEmitRequests.push({
        options,
        remaining: options.count,
        rate: options.spawnRate,
        accumulator: 0,
      });
    } else {
      // Spawn all at once
      for (let i = 0; i < options.count; i++) {
        this.spawnParticle(options);
      }
    }
  };

  private spawnParticle(
    options: EmitOptions,
    parent?: {position: Vector3; velocity: Vector3},
  ): void {
    const index = this.nextParticleIndex;
    const p = this.particles[index]!;

    // --- Apply properties, using overrides or defaults ---
    const lifetime =
      options.particleLifetime ?? this.defaultProps.particleLifetime;
    const randomness =
      options.lifetimeRandomness ?? this.defaultProps.lifetimeRandomness;
    p.maxLifetime = random(
      lifetime * (1 - randomness),
      lifetime * (1 + randomness),
    );
    if (p.maxLifetime <= 0) p.maxLifetime = 0.001; // prevent divide by zero
    p.lifetime = p.maxLifetime;
    p.gravity = options.gravity ?? this.defaultProps.gravity;

    p.colorCurve = normalizeColorCurve(
      options.color ?? this.defaultProps.colorCurve,
    );
    p.sizeCurve = normalizeSizeCurve(
      options.size ?? this.defaultProps.sizeCurve,
    );

    p.spawnEvents = options.spawnEvents ?? [];
    p.lastSpawnCheckProgress = 0;

    // --- Set initial state ---
    p.isActive = true;
    p.position.set(
      parent?.position.x ?? options.x,
      parent?.position.y ?? options.y,
      this.zIndex,
    );
    p.rotationZ = Math.random() * 2 * Math.PI;

    const velocityMultiplier =
      options.velocityMultiplier ?? this.defaultProps.velocityMultiplier;
    const upwardBias = options.upwardBias ?? this.defaultProps.upwardBias;
    const [vx, vy, vz] = randomOnSphere(velocityMultiplier);

    const baseVelX = options.baseVelocity?.x ?? parent?.velocity.x ?? 0;
    const baseVelY = options.baseVelocity?.y ?? parent?.velocity.y ?? 0;

    p.velocity.set(baseVelX + vx, baseVelY + vy + upwardBias, vz);

    this.nextParticleIndex = (index + 1) % this.defaultProps.maxParticles;
  }

  private processEmitters(delta: number): void {
    this.ongoingEmitRequests = this.ongoingEmitRequests.filter((req) => {
      req.accumulator += req.rate * delta;
      const numToSpawn = Math.floor(req.accumulator);

      if (numToSpawn > 0) {
        const count = Math.min(numToSpawn, req.remaining);
        for (let i = 0; i < count; i++) {
          this.spawnParticle(req.options);
        }
        req.remaining -= count;
        req.accumulator -= numToSpawn;
      }

      return req.remaining > 0;
    });
  }

  public update(delta: number, mesh: InstancedMesh): void {
    // Create the color buffer if it does not exist
    if (!mesh.instanceColor) mesh.setColorAt(0, this.tmpColor);

    let needsMatrixUpdate = false;
    let needsColorUpdate = false;

    this.processEmitters(delta);

    this.particles.forEach((p, index) => {
      if (!p.isActive) {
        return;
      }

      p.lifetime -= delta;

      if (p.lifetime <= 0) {
        p.isActive = false;

        // Perform one final render update to scale the particle to zero,
        // making it invisible.
        this.tmpObject.scale.setScalar(0);
        this.tmpObject.updateMatrix();
        mesh.setMatrixAt(index, this.tmpObject.matrix);

        // We must flag that the instance matrix has changed.
        needsMatrixUpdate = true;

        // Now we can safely skip the rest of the logic for this particle.
        return;
      }

      // --- Physics ---
      p.velocity.y -= p.gravity * delta;
      this.tmpVec3.copy(p.velocity).multiplyScalar(delta);
      p.position.add(this.tmpVec3);

      const progress = 1 - p.lifetime / p.maxLifetime;

      // --- Child Spawning ---
      if (p.spawnEvents.length > 0) {
        p.spawnEvents.forEach((event) => {
          if (progress >= event.time && p.lastSpawnCheckProgress < event.time) {
            this.emit({
              ...event.options,
              count: event.count,
              x: p.position.x,
              y: p.position.y,
              baseVelocity: {x: p.velocity.x * 0.5, y: p.velocity.y * 0.5},
            });
          }
        });
        p.lastSpawnCheckProgress = progress;
      }

      // --- Visuals ---
      const size = interpolateCurve(
        progress,
        p.sizeCurve,
        (a, b, t) => a + (b - a) * t,
      );
      const color = interpolateCurve(progress, p.colorCurve, (a, b, t) =>
        this.tmpColor.copy(a).lerp(b, t),
      );

      // The redundant `if (p.isActive)` check is removed, as this block is
      // now only reached by active particles.
      this.tmpObject.position.copy(p.position);
      this.tmpObject.rotation.set(0, 0, p.rotationZ);
      this.tmpObject.scale.setScalar(size);

      this.tmpObject.updateMatrix();
      mesh.setMatrixAt(index, this.tmpObject.matrix);
      mesh.setColorAt(index, color);

      needsMatrixUpdate = true;
      needsColorUpdate = true;
    });

    if (needsMatrixUpdate) {
      mesh.instanceMatrix.needsUpdate = true;
    }
    if (needsColorUpdate && mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true;
    }
  }

  // A method to reset the mesh when maxParticles changes.
  public initializeMesh(mesh: InstancedMesh) {
    this.tmpObject.scale.setScalar(0);
    this.tmpObject.updateMatrix();
    for (let i = 0; i < this.defaultProps.maxParticles; i++) {
      mesh.setMatrixAt(i, this.tmpObject.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }
}
